import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { DatePicker } from 'antd';
import moment from 'moment';
import reportingService from '../../../services/reportingService';

const { RangePicker } = DatePicker;

const HemodialysisReportModal = ({ show, handleClose, patientList = [] }) => {
  const [reportType, setReportType] = useState('single');
  const [selectedPatient, setSelectedPatient] = useState('');
  const [selectedPatients, setSelectedPatients] = useState([]);
  const [sessionDate, setSessionDate] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (show) {
      setReportType('single');
      setSelectedPatient('');
      setSelectedPatients([]);
      setSessionDate(null);
      setDateRange([null, null]);
      setError('');
    }
  }, [show]);

  const handleReportTypeChange = (e) => {
    setReportType(e.target.value);
    setError('');
  };

  const handlePatientChange = (e) => {
    setSelectedPatient(e.target.value);
    setError('');
  };

  const handleMultiPatientChange = (e) => {
    const value = Array.from(e.target.selectedOptions, option => option.value);
    setSelectedPatients(value);
    setError('');
  };

  const handleSessionDateChange = (date) => {
    setSessionDate(date);
    setError('');
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setError('');
  };

  const validateForm = () => {
    if (reportType === 'single') {
      if (!selectedPatient) {
        setError('Please select a patient for single patient report');
        return false;
      }
    } else if (reportType === 'multi') {
      // Multi-patient report can work with date range only
      if (!dateRange[0] && !dateRange[1] && selectedPatients.length === 0) {
        setError('Please select either date range or specific patients for multi-patient report');
        return false;
      }
    }
    return true;
  };

  const downloadPDF = (blob, filename) => {
    try {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error('Error downloading PDF:', error);
      setError('Failed to download PDF: ' + error.message);
      return false;
    }
  };

  const handleGenerate = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      let response;
      let filename;

      if (reportType === 'single') {
        const dateStr = sessionDate ? sessionDate.format('YYYY-MM-DD') : null;
        response = await reportingService.generateHemodialysisSinglePatientPDF(selectedPatient, dateStr);

        const patientName = patientList.find(p => p.id === selectedPatient)?.name || 'Patient';
        const datePrefix = dateStr ? `_${dateStr}` : '';
        filename = `hemodialysis_${patientName.replace(/\s+/g, '_')}${datePrefix}_${moment().format('YYYYMMDD_HHmmss')}.pdf`;

      } else {
        const startDate = dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : null;
        const endDate = dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : null;
        const patientIds = selectedPatients.length > 0 ? selectedPatients : null;

        response = await reportingService.generateHemodialysisMultiPatientPDF(startDate, endDate, patientIds);

        const datePrefix = startDate && endDate ? `_${startDate}_to_${endDate}` : '';
        filename = `hemodialysis_multi_patient${datePrefix}_${moment().format('YYYYMMDD_HHmmss')}.pdf`;
      }

      if (response.success && response.data) {
        const downloadSuccess = downloadPDF(response.data, filename);
        if (downloadSuccess) {
          handleClose();
        }
      } else {
        setError('Failed to generate report: ' + (response.message || 'Unknown error'));
      }

    } catch (error) {
      console.error('Error generating hemodialysis report:', error);
      setError('Failed to generate report: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getAvailablePatients = () => {
    // Filter patients that have hemodialysis data if needed
    return patientList.map(patient => ({
      id: patient.id || patient._id,
      name: patient.full_name || patient.name || 'Patient Name Unavailable',
      mrn: patient.mrn || patient.mrn_permanent
    }));
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" className="hemodialysis-report-modal">
      <Modal.Header closeButton>
        <Modal.Title>Generate Hemodialysis Report</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <Form>
          {/* Report Type Selection */}
          <Form.Group className="mb-3">
            <Form.Label><strong>Report Type</strong></Form.Label>
            <Form.Select value={reportType} onChange={handleReportTypeChange}>
              <option value="single">Single Patient Session Report</option>
              <option value="multi">Multi-Patient Summary Report</option>
            </Form.Select>
            <Form.Text className="text-muted">
              {reportType === 'single'
                ? 'Generate detailed report for one patient matching clinical format'
                : 'Generate summary table for multiple patients with statistics'
              }
            </Form.Text>
          </Form.Group>

          {/* Single Patient Options */}
          {reportType === 'single' && (
            <>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Select Patient</strong></Form.Label>
                    <Form.Select value={selectedPatient} onChange={handlePatientChange}>
                      <option value="">Choose patient...</option>
                      {getAvailablePatients().map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.name} {patient.mrn ? `(${patient.mrn})` : ''}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Session Date (Optional)</strong></Form.Label>
                    <DatePicker
                      style={{ width: '100%' }}
                      value={sessionDate}
                      onChange={handleSessionDateChange}
                      placeholder="Select date"
                      format="DD-MM-YYYY"
                      getPopupContainer={(trigger) => trigger.parentElement}
                    />
                    <Form.Text className="text-muted">
                      Leave empty for latest session
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          {/* Multi-Patient Options */}
          {reportType === 'multi' && (
            <>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Date Range (Optional)</strong></Form.Label>
                    <RangePicker
                      style={{ width: '100%' }}
                      value={dateRange}
                      onChange={handleDateRangeChange}
                      format="DD-MM-YYYY"
                      placeholder={['Start Date', 'End Date']}
                      getPopupContainer={(trigger) => trigger.parentElement}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Specific Patients (Optional)</strong></Form.Label>
                    <Form.Select multiple value={selectedPatients} onChange={handleMultiPatientChange}>
                      {getAvailablePatients().map((patient) => (
                        <option key={patient.id} value={patient.id}>
                          {patient.name} {patient.mrn ? `(${patient.mrn})` : ''}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Hold Ctrl/Cmd to select multiple patients. Leave empty for all patients.
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </>
          )}

          {/* Information Note */}
          <Alert variant="info" className="mt-3">
            <strong>Note:</strong> Reports are generated from hemodialysis form submissions.
            Ensure the patient has completed Pre-HD, During-HD, and Post-HD forms for complete reports.
          </Alert>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleGenerate}
          disabled={loading}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Generating...
            </>
          ) : (
            'Generate Report'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HemodialysisReportModal;